import React, { useState, useEffect } from "react";
import axios from "axios";
import { NavLink, useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Sidebar() {
  const navigate = useNavigate();

  /* ===================== DROPDOWN STATES ===================== */
  const [open, setOpen] = useState({
    forms: false,
    tables: false,
    duty: false,
    history: false,
  });

  const toggle = (key) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ===================== UNREAD COUNT ===================== */
  const [unreadCount, setUnreadCount] = useState(0);
  const [id, setId] = useState(1);

  const role = localStorage.getItem("role");
  const token =
    role === "admin"
      ? localStorage.getItem("adminToken")
      : localStorage.getItem("userToken");

  useEffect(() => {
    if (!token) return;

    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/notification/${id}/${role}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const unread = Array.isArray(res.data)
          ? res.data.filter((n) => !n.notificationStatus).length
          : 0;

        setUnreadCount(unread);
      } catch (err) {
        console.error("Unread count error:", err);
      }
    };

    fetchUnread();
  }, [token]);

  /* ===================== LOGOUT ===================== */
  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };

  /* ===================== RESPONSIVE: Hide sidebar below 991px ===================== */
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // If screen width <= 991px, do not render the sidebar at all
  if (windowWidth <= 991) {
    return null;
  }

  return (
    <aside className="sidebar-container">
      {/* Logo */}
      <div className="logo-section">
        <NavLink to="/admindashboard" className="logo-link">
          <span className="logo-text">Admin Panel</span>
        </NavLink>
      </div>

      {/* Menu */}
      <nav className="menu-nav">
        <ul className="menu-list">
          {/* Dashboard */}
          <li className="menu-item">
            <NavLink to="/admindashboard" className="menu-link" activeClassName="active">
              <i className="fas fa-home"></i>
              <span>Dashboard</span>
            </NavLink>
          </li>

          {/* Notifications Section */}
          <li className="section-title">Notifications</li>
          <li className="menu-item">
            <NavLink to="/notify" className="menu-link" activeClassName="active">
              <div className="icon-wrapper">
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </div>
              <span>Notifications</span>
            </NavLink>
          </li>

          {/* Manage Forms Dropdown */}
          <li
            className={`dropdown-header ${open.forms ? "open" : ""}`}
            onClick={() => toggle("forms")}
          >
            <span>Manage Forms</span>
            <i className={`fas fa-chevron-${open.forms ? "up" : "down"} arrow`}></i>
          </li>

          {open.forms && (
            <div className="dropdown-content">
              <li className="menu-item sub">
                <NavLink to="/userform" className="menu-link" activeClassName="active">
                  <i className="fas fa-pen-square"></i>
                  <span>Manager Registration Form</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/vipform" className="menu-link" activeClassName="active">
                  <i className="fas fa-pen-square"></i>
                  <span>VIP Registration Form</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/guardform" className="menu-link" activeClassName="active">
                  <i className="fas fa-pen-square"></i>
                  <span>Officer Registration Form</span>
                </NavLink>
              </li>
              {/* <li className="menu-item sub">
                <NavLink to="/designation" className="menu-link" activeClassName="active">
                  <i className="fas fa-pen-square"></i>
                  <span>Security Registration Form</span>
                </NavLink>
              </li> */}
            </div>
          )}

          {/* Tables Dropdown */}
          <li
            className={`dropdown-header ${open.tables ? "open" : ""}`}
            onClick={() => toggle("tables")}
          >
            <span>Tables</span>
            <i className={`fas fa-chevron-${open.tables ? "up" : "down"} arrow`}></i>
          </li>

          {open.tables && (
            <div className="dropdown-content">
              <li className="menu-item sub">
                <NavLink to="/viplist" className="menu-link" activeClassName="active">
                  <i className="fas fa-table"></i>
                  <span>VIP Table</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/guardlist" className="menu-link" activeClassName="active">
                  <i className="fas fa-table"></i>
                  <span>Officers Table</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/selfregistrationcheck" className="menu-link" activeClassName="active">
                  <i className="fas fa-table"></i>
                  <span>Activate Users</span>
                </NavLink>
              </li>
            </div>
          )}

          {/* Duty Management Dropdown */}
          <li
            className={`dropdown-header ${open.duty ? "open" : ""}`}
            onClick={() => toggle("duty")}
          >
            <span>Duty Management</span>
            <i className={`fas fa-chevron-${open.duty ? "up" : "down"} arrow`}></i>
          </li>

          {open.duty && (
            <div className="dropdown-content">
              <li className="menu-item sub">
                <NavLink to="/adminrequestaccept" className="menu-link" activeClassName="active">
                  <i className="fas fa-check-circle"></i>
                  <span>Duty Accept/Reject</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/incidents" className="menu-link" activeClassName="active">
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Incidents</span>
                </NavLink>
              </li>
            </div>
          )}

          {/* History Dropdown */}
          <li
            className={`dropdown-header ${open.history ? "open" : ""}`}
            onClick={() => toggle("history")}
          >
            <span>History</span>
            <i className={`fas fa-chevron-${open.history ? "up" : "down"} arrow`}></i>
          </li>

          {open.history && (
            <div className="dropdown-content">
              <li className="menu-item sub">
                <NavLink to="/updatehistory" className="menu-link" activeClassName="active">
                  <i className="fas fa-history"></i>
                  <span>Updation History</span>
                </NavLink>
              </li>
              <li className="menu-item sub">
                <NavLink to="/dutyhistory" className="menu-link" activeClassName="active">
                  <i className="fas fa-history"></i>
                  <span>Officer-Duty-History</span>
                </NavLink>
              </li>
            </div>
          )}

          {/* Others Section */}
          <li className="section-title">Others</li>
          <li className="menu-item">
            <NavLink to="/adminprofile" className="menu-link" activeClassName="active">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </NavLink>
          </li>
          {/* <li className="menu-item">
            <NavLink to="/createsecuritygroup" className="menu-link" activeClassName="active">
              <i className="fas fa-cog"></i>
              <span>Settings</span>
            </NavLink>
          </li> */}
          <li className="menu-item">
            <NavLink to="/login" onClick={handleLogout} className="menu-link logout" activeClassName="active">
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </NavLink>
          </li>
        </ul>
      </nav>

      {/* Professional & Responsive CSS */}
      <style jsx>{`
        .sidebar-container {
          width: 280px;
          min-height: 100vh;
          background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
          color: #e2e8f0;
          position: fixed;
          top: 0;
          left: 0;
          overflow-y: auto;
          box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
          transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1000;
        }

        /* Hide completely on screens ≤ 991px (handled in JS) */
        @media (max-width: 991px) {
          .sidebar-container {
            display: none;
          }
        }

        // /* Collapse to icons on larger tablets (992px - 1024px) */
        // @media (min-width: 992px) and (max-width: 1200px) {
        //   .sidebar-container {
        //     width: 78px;
        //   }
        //   .sidebar-container:hover {
        //     width: 280px;
        //   }
        //   .sidebar-container .logo-text,
        //   .sidebar-container span:not(.notification-badge),
        //   .sidebar-container .section-title,
        //   .sidebar-container .arrow {
        //     opacity: 0;
        //     visibility: hidden;
        //     transition: opacity 0.2s ease;
        //   }
        //   .sidebar-container:hover .logo-text,
        //   .sidebar-container:hover span:not(.notification-badge),
        //   .sidebar-container:hover .section-title,
        //   .sidebar-container:hover .arrow {
        //     opacity: 1;
        //     visibility: visible;
        //     transition: opacity 0.3s ease 0.2s;
        //   }
        // }

        .logo-section {
          padding: 28px 20px;
          text-align: center;
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logo-link {
          text-decoration: none;
        }

        .logo-text {
          font-size: 26px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: 0.5px;
        }

        .menu-nav {
          padding: 20px 0;
        }

        .menu-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1.8px;
          color: #64748b;
          padding: 20px 24px 8px;
          transition: opacity 0.3s;
        }

        .menu-item {
          margin-bottom: 4px;
        }

        .menu-link {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 14px 24px;
          color: #cbd5e1;
          text-decoration: none;
          font-weight: 500;
          border-radius: 0 30px 30px 0;
          margin-right: 16px;
          transition: all 0.35s ease;
          position: relative;
        }

        .menu-link i {
          font-size: 19px;
          width: 26px;
          text-align: center;
        }

        .menu-link:hover {
          background: rgba(59, 130, 246, 0.2);
          color: #ffffff;
          transform: translateX(10px);
        }

        .menu-link:hover i {
          color: #60a5fa;
        }

        .menu-link.active {
          background: linear-gradient(90deg, #3b82f6, rgba(59, 130, 246, 0.1));
          color: #ffffff;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .menu-link.active i {
          color: #ffffff;
        }

        .icon-wrapper {
          position: relative;
        }

        .notification-badge {
          position: absolute;
          top: -8px;
          right: -10px;
          background: #ef4444;
          color: white;
          font-size: 11px;
          font-weight: bold;
          min-width: 20px;
          height: 20px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: pulse 2s infinite;
          box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .dropdown-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 24px;
          font-weight: 600;
          color: #e2e8f0;
          cursor: pointer;
          border-radius: 0 30px 30px 0;
          margin-right: 16px;
          transition: all 0.35s ease;
        }

        .dropdown-header:hover {
          background: rgba(59, 130, 246, 0.15);
          transform: translateX(8px);
        }

        .dropdown-header.open {
          background: rgba(59, 130, 246, 0.25);
          color: #60a5fa;
        }

        .arrow {
          font-size: 13px;
          transition: transform 0.3s ease;
        }

        .dropdown-content {
          overflow: hidden;
          animation: slideDown 0.4s ease-out;
        }

        @keyframes slideDown {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 300px; }
        }

        .sub .menu-link {
          padding-left: 70px;
          font-size: 14.5px;
        }

        .sub .menu-link:hover {
          background: rgba(59, 130, 246, 0.1);
          color: #93c5fd;
        }

        .logout:hover {
          background: rgba(239, 68, 68, 0.25) !important;
          color: #fca5a5 !important;
        }

        .logout:hover i {
          color: #ef4444 !important;
        }
      `}</style>
    </aside>
  );
}







// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { NavLink, useNavigate } from "react-router-dom";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function Sidebar() {
//   const navigate = useNavigate();

//   /* ===================== DROPDOWN STATES ===================== */
//   const [open, setOpen] = useState({
//     forms: false,
//     tables: false,
//     duty: false,
//     history: false,
//   });

//   const toggle = (key) =>
//     setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

//   /* ===================== UNREAD COUNT ===================== */
//   const [unreadCount, setUnreadCount] = useState(0);
//   const [id, setId] = useState(1);

//   const role = localStorage.getItem("role");
//   const token =
//     role === "admin"
//       ? localStorage.getItem("adminToken")
//       : localStorage.getItem("userToken");

//   useEffect(() => {
//     if (!token) return;

//     // Define fetch function INSIDE useEffect to avoid ESLint warning
//     const fetchUnread = async () => {
//       try {
//         const res = await axios.get( `${BASE_URL}/api/notification/${id}/${role}`, {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const unread = Array.isArray(res.data)
//           ? res.data.filter((n) => !n.notificationStatus).length
//           : 0;

//         setUnreadCount(unread);
//       } catch (err) {
//         console.error("Unread count error:", err);
//       }
//     };

//     // Call initially
//     fetchUnread();

//     // // Call repeatedly
//     // const interval = setInterval(fetchUnread, 5000);

//     // return () => clearInterval(interval);
//   }, [token]);

//   /* ===================== LOGOUT ===================== */
//   const handleLogout = () => {
//     localStorage.clear();
//     sessionStorage.clear();
//     navigate("/login");
//   };

//   return (
//     <div style={styles.sidebarContainer} className="sidebar">
//       {/* HOVER ANIMATIONS */}
//       <style>
//         {`
//           .menu-link {
//             background: linear-gradient(to right, #a6f91fff 50%, transparent 50%);
//             background-size: 200% 100%;
//             background-position: right bottom;
//             transition: all 0.4s ease;
//           }

//           .menu-link:hover {
//             background-position: left bottom;
//             color: #1e73be !important;
//           }

//           .menu-link:hover i {
//             color: #1e73be !important;
//           }

//           .dropdown-forms:hover {
//             border-left: 6px solid #f5e5a5ff;
            
//           }

//           .dropdown-tables:hover {
//             border-left: 8px solid #fafffeff;
            
//           }

//           .dropdown-duty:hover {
//             border-left: 4px solid #faf9f8ff;
            
//           }

//           .dropdown-history:hover {
//             border-left: 4px solid #f3f6eeff;
            
//           }
//         `}
//       </style>

//       {/* LOGO */}
//       <div style={styles.logoBox}>
//         <NavLink to="/admindashboard" style={styles.logoLink}>
//           <span style={styles.logoText}>Admin Panel</span>
//         </NavLink>
//       </div>

//       {/* MENU */}
//       <div style={styles.menuWrapper}>
//         <ul style={styles.menuList}>

//           {/* Dashboard */}
//           <li style={styles.menuItem}>
//             <NavLink to="/admindashboard" style={styles.menuLink} className="menu-link mt-3">
//               <i className="fas fa-home" style={styles.icon}></i>
//               <span>Dashboard</span>
//             </NavLink>
//           </li>

//           {/* ===================== NOTIFICATIONS ===================== */}
//           <li style={styles.sectionTitle}>Notifications</li>

//           <li style={styles.menuItem}>
//             <NavLink to="/notify" style={styles.menuLink} className="menu-link">
//               <div style={{ position: "relative" }}>
//                 <i className="fas fa-bell" style={styles.icon}></i>

//                 {unreadCount > 0 && (
//                   <span style={styles.badge}>{unreadCount}</span>
//                 )}
//               </div>
//               <span>Notifications</span>
//             </NavLink>
//           </li>

//           {/* ===================== MANAGE FORMS ===================== */}
//           <li
//             style={styles.sectionTitleForms}
//             className="dropdown-forms"
//             onClick={() => toggle("forms")}
//           >
//             Manage Forms
//             <i className={`fas fa-chevron-${open.forms ? "up" : "down"}`} style={styles.arrowForms}></i>
//           </li>

//           {open.forms && (
//             <>
//               <li style={styles.menuItem}>
//                 <NavLink to="/userform" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-pen-square" style={styles.icon}></i>
//                   <span>Manager Registration Form</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/vipform" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-pen-square" style={styles.icon}></i>
//                   <span>VIP Registration Form</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/guardform" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-pen-square" style={styles.icon}></i>
//                   <span>Officer Registration Form</span>
//                 </NavLink>
//               </li>
//               <li style={styles.menuItem}>
//                 <NavLink to="/designation" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-pen-square" style={styles.icon}></i>
//                   <span>Security Registration Form</span>
//                 </NavLink>
//               </li>

//             </>
//           )}

//           {/* ===================== TABLES ===================== */}
//           <li
//             style={styles.sectionTitleTables}
//             className="dropdown-tables"
//             onClick={() => toggle("tables")}
//           >
//             Tables
//             <i className={`fas fa-chevron-${open.tables ? "up" : "down"}`} style={styles.arrowTables}></i>
//           </li>

//           {open.tables && (
//             <>
//               <li style={styles.menuItem}>
//                 <NavLink to="/viplist" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-table" style={styles.icon}></i>
//                   <span>VIP Table</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/guardlist" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-table" style={styles.icon}></i>
//                   <span>Officers Table</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/selfregistrationcheck" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-table" style={styles.icon}></i>
//                   <span>Activate Users</span>
//                 </NavLink>
//               </li>
//             </>
//           )}

//           {/* ===================== DUTY ===================== */}
//           <li
//             style={styles.sectionTitleDuty}
//             className="dropdown-duty"
//             onClick={() => toggle("duty")}
//           >
//             Duty-Management
//             <i className={`fas fa-chevron-${open.duty ? "up" : "down"}`} style={styles.arrowDuty}></i>
//           </li>

//           {open.duty && (
//             <>
//               <li style={styles.menuItem}>
//                 <NavLink to="/adminrequestaccept" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-check-circle" style={styles.icon}></i>
//                   <span>Duty Accept/Reject</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/incidents" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-exclamation-triangle" style={styles.icon}></i>
//                   <span>Incidents</span>
//                 </NavLink>
//               </li>
//             </>
//           )}

//           {/* ===================== HISTORY ===================== */}
//           <li
//             style={styles.sectionTitleHistory}
//             className="dropdown-history"
//             onClick={() => toggle("history")}
//           >
//             History
//             <i className={`fas fa-chevron-${open.history ? "up" : "down"}`} style={styles.arrowHistory}></i>
//           </li>

//           {open.history && (
//             <>
//               <li style={styles.menuItem}>
//                 <NavLink to="/updatehistory" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-history" style={styles.icon}></i>
//                   <span>Updation History</span>
//                 </NavLink>
//               </li>

//               <li style={styles.menuItem}>
//                 <NavLink to="/dutyhistory" style={styles.menuLink} className="menu-link">
//                   <i className="fas fa-history" style={styles.icon}></i>
//                   <span>Officer-Duty-History</span>
//                 </NavLink>
//               </li>
//             </>
//           )}

//           {/* ===================== OTHERS ===================== */}
//           <li style={styles.sectionTitle}>Others</li>

//           <li style={styles.menuItem}>
//             <NavLink to="/adminprofile" style={styles.menuLink} className="menu-link">
//               <i className="fas fa-user" style={styles.icon}></i>
//               <span>My Profile</span>
//             </NavLink>
//           </li>

//           <li style={styles.menuItem}>
//             <NavLink to="/setting" style={styles.menuLink} className="menu-link">
//               <i className="fas fa-cog" style={styles.icon}></i>
//               <span>Settings</span>
//             </NavLink>
//           </li>

//           <li style={styles.menuItem}>
//             <NavLink to="/login" onClick={handleLogout} style={styles.menuLink} className="menu-link">
//               <i className="fas fa-sign-out-alt" style={styles.icon}></i>
//               <span>Logout</span>
//             </NavLink>
//           </li>

//         </ul>
//       </div>
//     </div>
//   );
// }

// /* ===================== STYLES ===================== */

// const styles = {
//   sidebarContainer: {
//     width: "260px",
//     height: "100vh",
//     background: "linear-gradient(135deg, rgba(155, 197, 237, 0.7), rgba(242, 247, 143, 0.9))",
//     backdropFilter: "blur(10px)",
//     borderRight: "1px solid rgba(255,255,255,0.3)",
//     boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
//     paddingTop: "20px",
//     position: "fixed",
//     overflowY: "auto",
//   },

//   logoBox: {
//     textAlign: "center",
//     marginBottom: "35px",
//     padding: "18px 0",
//     background: "linear-gradient(135deg, rgba(69, 159, 244, 0.7), rgba(241, 251, 43, 0.9)",
//     borderRadius: "14px",
//     width: "85%",
//     margin: "auto",
//   },

//   logoLink: { textDecoration: "none"},

//   logoText: {
//     fontSize: "26px",
//     fontWeight: "700",
//     color: "#3865d5ff",
//   },

//   menuWrapper: { padding: "0 10px" },
//   menuList: { listStyle: "none", padding: 0 },
//   menuItem: { marginBottom: "8px" },

//   menuLink: {
//     display: "flex",
//     alignItems: "center",
//     gap: "12px",
//     padding: "12px 15px",
//     borderRadius: "10px",
//     textDecoration: "none",
//     fontSize: "15px",
//     fontWeight: "600",
//     color: "#1e1e1e",
//   },

//   icon: { fontSize: "16px", color: "#1e73be" },

//   sectionTitle: {
//     fontSize: "13px",
//     fontWeight: "700",
//     color: "#1e73be",
//     margin: "20px 0 10px 10px",
//     textTransform: "uppercase",
//   },

//   sectionTitleForms: {
//     fontSize: "16px",
//     fontWeight: "700",
//     color: "  #e37f27ff",
//     margin: "20px 10px 10px 10px",
//     cursor: "pointer",
//     padding: "6px 10px",
   
//     background: "rgba(84, 61, 236, 0.08)",
//     transition: "0.3s",
//   },

//   sectionTitleTables: {
//     fontSize: "16px",
//     fontWeight: "700",
//     color: "#20b2aa",
//     margin: "20px 10px 10px 10px",
//     cursor: "pointer",
//     padding: "6px 10px",
   
//     background: "rgba(84, 61, 236, 0.08)",
//     transition: "0.3s",
//   },

//   sectionTitleDuty: {
//     fontSize: "16px",
//     fontWeight: "700",
//     color: "#ff8c00",
//     margin: "20px 10px 10px 10px",
//     cursor: "pointer",
//     padding: "6px 10px",
   
//     background: "rgba(84, 61, 236, 0.08)",
//     transition: "0.3s",
//   },

//   sectionTitleHistory: {
//     fontSize: "16px",
//     fontWeight: "700",
//     color: "#28f68b",
//     margin: "20px 10px 10px 10px",
//     cursor: "pointer",
//     padding: "6px 10px",
   
//     background: "rgba(84, 61, 236, 0.08)",
//     transition: "0.3s",
//   },

//   arrowForms: { fontSize: "12px", marginLeft: "8px", color: "#6a5acd" },
//   arrowTables: { fontSize: "12px", marginLeft: "8px", color: "#20b2aa" },
//   arrowDuty: { fontSize: "12px", marginLeft: "8px", color: "#ff8c00" },
//   arrowHistory: { fontSize: "12px", marginLeft: "8px", color: "#28f68b" },

//   badge: {
//     position: "absolute",
//     top: "-6px",
//     right: "-10px",
//     background: "red",
//     color: "#fff",
//     borderRadius: "50%",
//     padding: "2px 7px",
//     fontSize: "11px",
//     fontWeight: "700",
//   },
// };
